import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatCalendar,
} from "@angular/material/datepicker";

import { DatePickerType } from "@bitwarden/web-vault/app/models/types/date-picker.types";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./calendar.component.scss";

export const dateUnitOptions = ["days", "weeks", "months", "years"] as const;
export type DateUnit = (typeof dateUnitOptions)[number];
export const DAYS: DateUnit = "days";
export const WEEKS: DateUnit = "weeks";
export const MONTHS: DateUnit = "months";
export const YEARS: DateUnit = "years";

export const TIME_DIRECTION_LATER = "later";
export const TIME_DIRECTION_AGO = "ago";
export type TimeDirection = typeof TIME_DIRECTION_LATER | typeof TIME_DIRECTION_AGO;

@Component({
  selector: "app-calendar",
  templateUrl: "./calendar.component.html",
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
  ],
})
export class CalendarComponent implements OnInit, AfterViewInit {
  relativeDateValue: number;
  initialDateRange: DateRange<Date>;
  topLabel: string;
  pickerLabel: string;
  waysToSetText: string;
  selectedDateUnit: DateUnit;
  selectedTimeDirection: TimeDirection;
  showCalendar = true;
  protected readonly today: Date = new Date();
  protected calendarHeight: string;

  @Input() pickerType: DatePickerType;
  @Input() dateUnitOptions: DateUnit[] = [...dateUnitOptions];
  @Input() timeDirectionOptions: TimeDirection[] = [TIME_DIRECTION_LATER, TIME_DIRECTION_AGO];
  @Input() dateRange: DateRange<Date>;
  @Input() originalDateRange: DateRange<Date>;
  @Input() currentDateFilter: (date: Date) => boolean;
  @Output() dateChangeEvent = new EventEmitter<DateRange<Date>>();
  // Fires on every in-calendar change (day click, jump-to, reset) for live preview.
  // Distinct from dateChangeEvent, which only fires on Confirm to actually apply.
  @Output() dateRangePreviewEvent = new EventEmitter<DateRange<Date>>();
  // maybe can remove, just emit the dateChangeEvent
  @Output() confirmEvent = new EventEmitter<void>();
  @Output() cancelEvent = new EventEmitter<void>();

  // Single-date mode: opt-in, leaves the default range behaviour (and existing consumers) untouched.
  @Input() singleDate = false;
  // Single-date sub-mode. false (default, e.g. app-date-field): apply on day click, no buttons.
  // true (e.g. today view): stage the pick, show Confirm/Cancel, apply only on Confirm.
  @Input() confirmMode = false;
  @Input() selectedDate: Date | null = null;
  @Output() dateSelected = new EventEmitter<Date>();
  // Staged pick in single-date mode; only applied on Confirm. Binding [selected] to
  // this makes mat-calendar deselect the previous day when a new one is clicked.
  pendingDate: Date | null = null;

  @ViewChild(MatCalendar, { read: ElementRef }) calendarRef!: ElementRef;

  ngOnInit() {
    this.today.setHours(0, 0, 0, 0);
    this.selectedDateUnit = this.dateUnitOptions[0];
    this.selectedTimeDirection = this.timeDirectionOptions[0];
    this.initialDateRange = this.dateRange;
    this.pendingDate = this.selectedDate;
    this.updateDisplayText();
  }

  ngAfterViewInit() {
    const calendarHeight = this.calendarRef.nativeElement.offsetHeight;
    this.calendarHeight = `${calendarHeight}px`;
  }

  rangeChanged(date: Date) {
    if (this.pickerType === "start") {
      const endDate = this.dateRange.end;
      this.dateRange = new DateRange(date, endDate && date > endDate ? date : endDate);
    } else if (this.pickerType === "end") {
      const startDate = this.dateRange.start;
      this.dateRange = new DateRange(startDate && date < startDate ? date : startDate, date);
    }
    this.updateRelativeControlsFromDate(date);
    this.dateRangePreviewEvent.emit(this.dateRange);
  }

  updateDisplayText() {
    const isStart = this.pickerType === "start";
    this.topLabel = isStart ? "start from" : "end on";
    this.pickerLabel = isStart ? "BEGINNING OF" : "END OF";
    this.waysToSetText = isStart ? "WAYS TO SET THE START DATE:" : "WAYS TO SET THE END DATE:";
  }

  selectDateUnit(option: string) {
    this.selectedDateUnit = option as DateUnit;
    this.updateRelativeDateRange();
  }

  selectTimeDirection(option: string) {
    this.selectedTimeDirection = option as TimeDirection;
    this.updateRelativeDateRange();
  }

  setRelatedDateValue(value: number) {
    // app-input's ControlValueAccessor echoes [value] back through (outputValue)
    // whenever relativeDateValue changes (see input.component.ts's ngOnChanges ->
    // formControl.setValue -> valueChanges -> outputValue.emit). That echo arrives
    // synchronously while this component's own view is still being checked, and
    // re-running updateRelativeDateRange (and its triggerCalendarRerender) for it
    // flips showCalendar after @if(showCalendar) was already checked this pass,
    // causing NG0100. Skip when the value hasn't actually changed.
    if (value != null && Number(value) === Number(this.relativeDateValue)) {
      return;
    }
    this.relativeDateValue = value;
    this.updateRelativeDateRange();
  }

  updateRelativeDateRange() {
    if (this.relativeDateValue != null && this.selectedDateUnit && this.selectedTimeDirection) {
      const baseDate = new Date(this.today);
      const calculatedDate = this.calculateRelativeDate(
        baseDate,
        this.relativeDateValue,
        this.selectedDateUnit,
        this.selectedTimeDirection,
      );
      if (!this.currentDateFilter || this.currentDateFilter(calculatedDate)) {
        if (this.singleDate) {
          this.pendingDate = calculatedDate;
        } else if (this.pickerType === "start") {
          this.dateRange = new DateRange(calculatedDate, this.dateRange?.end);
        } else if (this.pickerType === "end") {
          this.dateRange = new DateRange(this.dateRange?.start, calculatedDate);
        }
        this.dateRangePreviewEvent.emit(this.dateRange);
        this.triggerCalendarRerender();
      }
    }
  }

  private triggerCalendarRerender() {
    // This is to trigger the calendar to re-render, so that the updated startAt attribute in the calendar will work
    this.showCalendar = false;
    setTimeout(() => (this.showCalendar = true), 0);
  }

  calculateRelativeDate(
    baseDate: Date,
    value: number,
    unit: DateUnit,
    direction: TimeDirection,
  ): Date {
    const multiplier = direction === TIME_DIRECTION_LATER ? 1 : -1;
    const offset = value * multiplier;
    const actions: Record<DateUnit, () => void> = {
      days: () => baseDate.setDate(baseDate.getDate() + offset),
      weeks: () => baseDate.setDate(baseDate.getDate() + offset * 7),
      months: () => baseDate.setMonth(baseDate.getMonth() + offset),
      years: () => baseDate.setFullYear(baseDate.getFullYear() + offset),
    };
    actions[unit]?.();
    return baseDate;
  }

  updateRelativeControlsFromDate(date: Date) {
    const diffMs = date.getTime() - this.today.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    let value = Math.abs(diffDays);
    let unit: DateUnit = DAYS;
    // When only one direction is allowed (past/today=ago, future=later), use it — otherwise
    // a 0-day diff (picking today) wrongly reads as "later". Same result as past view.
    const direction: TimeDirection =
      this.timeDirectionOptions.length === 1
        ? this.timeDirectionOptions[0]
        : diffDays >= 0
          ? TIME_DIRECTION_LATER
          : TIME_DIRECTION_AGO;
    if (value % 7 === 0 && value !== 0) {
      value = value / 7;
      unit = WEEKS;
    }
    this.relativeDateValue = value;
    this.selectedDateUnit = unit;
    this.selectedTimeDirection = direction;
  }

  resetDateRange() {
    this.relativeDateValue = null;
    if (this.singleDate) {
      // Today view: reset always returns to today, not the last confirmed date.
      this.pendingDate = new Date(this.today);
      this.triggerCalendarRerender();
      return;
    }
    // if want to reset to last selected date range, use:
    // this.dateRange = this.initialDateRange;
    // if want to reset to original date range, use:
    this.dateRange = this.originalDateRange;
    this.dateRangePreviewEvent.emit(this.dateRange);
    this.triggerCalendarRerender();
  }

  onSingleSelect(date: Date) {
    if (this.confirmMode) {
      // Stage only; applied on Confirm. Rebinding [selected] deselects the old day.
      this.pendingDate = date;
      this.updateRelativeControlsFromDate(date);
      return;
    }
    // Default single-date: apply immediately (parent owns selectedDate and feeds it back).
    this.dateSelected.emit(date);
  }

  getStartAt(): Date | null {
    if (this.singleDate) {
      return this.pendingDate ?? this.selectedDate ?? null;
    }
    if (this.pickerType === "start" && this.dateRange?.start) {
      return this.dateRange.start;
    }
    if (this.pickerType === "end" && this.dateRange?.end) {
      return this.dateRange.end;
    }
    return null;
  }

  emitConfirmEvent() {
    if (this.singleDate) {
      if (this.pendingDate) {
        this.dateSelected.emit(this.pendingDate);
      }
      return;
    }
    this.dateChangeEvent.emit(this.dateRange);
    this.confirmEvent.emit();
  }

  emitCancelEvent() {
    this.cancelEvent.emit();
  }
}
